import React, { useState, useEffect, useRef, useCallback } from 'react';
import './OrbitTV.css';
import volumeIcon from '../img/preview/volume.png';
import noSoundIcon from '../img/preview/mute.png';

interface Channel {
  name: string;
  videoPath: string;
  hasAudio: boolean;
}

const channels: Channel[] = [
  { name: 'never celebrate july 4th with us', videoPath: '/video/fucked.mp4', hasAudio: true },
  { name: 'third culture @ nublu LES', videoPath: '/video/concert.mp4', hasAudio: false },
  { name: 'the video that got me into music production', videoPath: '/video/kanye.mp4', hasAudio: true },
  { name: 'sequoia - third culture', videoPath: '/video/sequoia.mp4', hasAudio: true },
  { name: 'the world is made up of people no smarter than you', videoPath: '/video/steve.mp4', hasAudio: true },
  { name: 'IM DONE WITH THE VLOGS?!', videoPath: '/video/vlogs.mp4', hasAudio: true },
  { name: 'sometimes i record myself making beats. this is me when i made \'la paix\'', videoPath: '/video/bts.mp4', hasAudio: true },
  { name: 'third culture @ masala mixtape, performing \'your orbit\'', videoPath: '/video/masala_mixtape.mp4', hasAudio: true },
  { name: 'old in orbit visual idea', videoPath: '/video/intro.mp4', hasAudio: false },
  { name: 'om shanti om (2007)', videoPath: 'https://pub-9964e4b8e27a44e5829974df103c112f.r2.dev/omshantiom.mp4', hasAudio: true }
];

// VHS transport dial. Signed "level": 0 = normal play (center), +1/+2/+3 = fast
// forward, -1/-2/-3 = rewind. Arrows move along the dial and land back on play (0).
// No pause. |level| = number of triangles shown; SCAN_RATES[|level|] = realtime multiplier.
const MAX_LEVEL = 3;
const SCAN_RATES = [0, 8, 24, 60];
const SCAN_TICK_MS = 120; // how often we hop while scanning (choppy on purpose)

const OrbitTV: React.FC = () => {
  const [currentChannelIndex, setCurrentChannelIndex] = useState(0);
  const [showChannelName, setShowChannelName] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [showAudioControl, setShowAudioControl] = useState(false);

  // Transport dial (0 = play)
  const [level, setLevel] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const audioControlTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Remember user's audio preference (only for videos with audio)
  const userAudioPreferenceRef = useRef<boolean>(true); // Default to muted

  const currentChannel = channels[currentChannelIndex];
  const isScanning = level !== 0;
  const isNormalPlaying = level === 0;

  const focusRoot = useCallback(() => {
    // Keep keyboard focus on the TV so arrows never leak to the mute button etc.
    requestAnimationFrame(() => rootRef.current?.focus());
  }, []);

  // Grab keyboard focus once the window opens.
  useEffect(() => {
    focusRoot();
  }, [focusRoot]);

  // Reset transport to normal play whenever the channel changes.
  useEffect(() => {
    setLevel(0);

    // Show channel name for 4.5 seconds, then fade for 0.5 seconds
    setShowChannelName(true);
    setIsFadingOut(false);

    const fadeTimer = setTimeout(() => setIsFadingOut(true), 4500);
    const hideTimer = setTimeout(() => setShowChannelName(false), 5000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [currentChannelIndex]);

  // Drive playback based on the transport level (normal play vs scan) and mute.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Audio only ever plays at normal speed; scanning is silent (true VHS).
    const muted = isScanning || !currentChannel.hasAudio || userAudioPreferenceRef.current;
    video.muted = muted;
    setIsMuted(muted);

    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (!isScanning) {
      video.playbackRate = 1;
      const play = video.play();
      if (play && typeof play.catch === 'function') play.catch(() => {});
      return;
    }

    // Scanning: hold native playback and hop currentTime on a timer.
    video.pause();
    const rate = SCAN_RATES[Math.abs(level)];
    const stepSeconds = Math.sign(level) * rate * (SCAN_TICK_MS / 1000);
    scanIntervalRef.current = setInterval(() => {
      const v = videoRef.current;
      if (!v || !v.duration || Number.isNaN(v.duration)) return;
      let t = v.currentTime + stepSeconds;
      if (t >= v.duration) t -= v.duration; // wrap past the end
      if (t < 0) t += v.duration; // wrap past the start
      v.currentTime = t;
    }, SCAN_TICK_MS);

    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
    };
  }, [level, isScanning, currentChannelIndex, currentChannel.hasAudio]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setLevel((l) => Math.min(MAX_LEVEL, l + 1));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setLevel((l) => Math.max(-MAX_LEVEL, l - 1));
    }
  };

  const handleVideoClick = () => {
    focusRoot();
    const nextIndex = (currentChannelIndex + 1) % channels.length;
    setCurrentChannelIndex(nextIndex);
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    focusRoot();
    // Mute toggle only means anything while playing normally with audio.
    if (videoRef.current && currentChannel.hasAudio && isNormalPlaying) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
      userAudioPreferenceRef.current = newMutedState;
    }
  };

  const handleMouseMove = () => {
    if (!currentChannel.hasAudio) return;

    setShowAudioControl(true);

    if (audioControlTimeoutRef.current) {
      clearTimeout(audioControlTimeoutRef.current);
    }

    audioControlTimeoutRef.current = setTimeout(() => {
      setShowAudioControl(false);
    }, 2000);
  };

  const handleTouchStart = () => {
    if (!currentChannel.hasAudio) return;

    setShowAudioControl(true);

    if (audioControlTimeoutRef.current) {
      clearTimeout(audioControlTimeoutRef.current);
    }

    audioControlTimeoutRef.current = setTimeout(() => {
      setShowAudioControl(false);
    }, 2000);
  };

  const dismissWelcome = () => {
    setShowWelcome(false);
    focusRoot();
  };

  // VHS scan glyph: |level| triangles pointing the scan direction.
  const dir = level < 0 ? 'left' : 'right';
  const triCount = Math.abs(level);

  return (
    <div
      className="orbit-tv"
      ref={rootRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseDown={focusRoot}
    >
      {/* Welcome Modal */}
      {showWelcome && (
        <div className="orbit-tv-welcome-overlay" onClick={dismissWelcome}>
          <div className="orbit-tv-welcome-modal" onClick={(e) => e.stopPropagation()}>
            <div className="orbit-tv-welcome-text">
              tap to change channels
              <br />
              ← → = rewind / fast-forward
            </div>
            <button className="orbit-tv-welcome-dismiss" onClick={dismissWelcome}>
              Got it
            </button>
          </div>
        </div>
      )}

      <div
        className="orbit-tv-video-container"
        onClick={handleVideoClick}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
      >
        <video
          ref={videoRef}
          src={currentChannel.videoPath}
          className="orbit-tv-video"
          autoPlay
          muted={isMuted}
          loop
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          x5-video-player-type="h5"
          x5-video-player-fullscreen="false"
        />

        {/* Channel Name Overlay */}
        {showChannelName && (
          <div className={`orbit-tv-channel-overlay ${isFadingOut ? 'fade-out' : ''}`}>
            <div className="orbit-tv-channel-gradient"></div>
            <div className="orbit-tv-channel-name">
              {currentChannel.name}
            </div>
          </div>
        )}

        {/* VHS scan indicator (top-right, only while skipping) */}
        <div className={`orbit-tv-transport ${isScanning ? 'visible' : ''}`}>
          {Array.from({ length: triCount }).map((_, i) => (
            <span key={i} className={`orbit-tv-tri ${dir}`} />
          ))}
        </div>

        {/* Audio Control */}
        {currentChannel.hasAudio && (
          <button
            className={`orbit-tv-audio-control ${showAudioControl ? 'visible' : ''}`}
            onClick={toggleMute}
            tabIndex={-1}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            <img
              src={isMuted ? noSoundIcon : volumeIcon}
              alt={isMuted ? 'Muted' : 'Volume'}
              className="orbit-tv-audio-icon"
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default OrbitTV;
